import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Search, SearchPage } from 'tms-lib';
import { lineExceptionColour } from '../../constants/line-exception-reasons';
import { TmsWebBaseEditService } from '../base';
import { SalesOrderBoardLine, SalesOrderLineException, SalesOrderLineExceptionFilter } from '../../models';

// Sales → Sales Order Line Exceptions (Sales Order What's Happened, requirement 7). The list's rows
// come from SalesOrderLine/SearchExceptions; a row opens the line audit, not this service's edit.
@Injectable({
  providedIn: 'root',
})
export class SalesOrderLineExceptionService extends TmsWebBaseEditService<
  SalesOrderLineException,
  SalesOrderLineException
> {
  constructor() {
    super('SalesOrderLine', $localize`Sales Order Line Exception`);
  }

  // app-search-grid sends its whole search-form model, so the Exceptions only tick arrives as
  // exceptionsOnly. Before the form has put its default in (the first search after the saved
  // search is restored), an unset tick means ticked - the API's own default too.
  // Each row's colour is replaced by its reason's (lineExceptionColour): the list tints by why a
  // line looks stuck, not by its Order Line Status, and a line with no reason is green.
  override searchPage(model?: Search & { exceptionsOnly?: boolean }): Observable<SearchPage<SalesOrderLineException>> {
    return this.httpService
      .post<SearchPage<SalesOrderLineException>>(`${this.apiUrl}/SearchExceptions`, {
        ...model,
        search: model?.search?.trim(),
        exceptionsOnly: model?.exceptionsOnly ?? true,
      })
      .pipe(
        map((page: SearchPage<SalesOrderLineException>) => ({
          ...page,
          results: (page?.results ?? []).map((row: SalesOrderLineException) => ({
            ...row,
            colour: lineExceptionColour(row.position?.reasons),
          })),
        })),
      );
  }

  // Sales → Sales Order Board (Sales Order Board D-9): every line the Exceptions list can show for
  // these filters, lean, each with its reasons when the Exceptions-only list would show it.
  exceptionBoard(filter: SalesOrderLineExceptionFilter): Observable<SalesOrderBoardLine[]> {
    return this.httpService.post<SalesOrderBoardLine[]>(`${this.apiUrl}/ExceptionBoard`, {
      fromDate: filter?.fromDate || null,
      toDate: filter?.toDate || null,
      customerId: filter?.customerId || null,
    });
  }

  // From after To (D-6, D-14): the board and the list refuse to search on it. Days are compared, so
  // the same day either way round is a valid one-day range.
  dateRangeReversed(filter: SalesOrderLineExceptionFilter | null | undefined): boolean {
    const day = (value: Date | string | null | undefined) => {
      if (!value) {
        return null;
      }
      const date = value instanceof Date ? value : new Date(value);
      return isNaN(date.getTime()) ? null : Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    };
    const from = day(filter?.fromDate);
    const to = day(filter?.toDate);
    return from !== null && to !== null && from > to;
  }
}
